
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { SeoResult } from "../types";
import { useIsMobile } from "@/hooks/use-mobile";

interface OverviewTabProps {
  result: SeoResult;
}

export const OverviewTab = ({ result }: OverviewTabProps) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Total Backlinks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{result.backlinks.toLocaleString()}</div>
            <CardDescription>
              {result.backlinks < 1000 ? "Below average for your industry" : "Good backlink profile"}
            </CardDescription>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Ranking Keywords</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{result.keywords.length * (Math.floor(Math.random() * 100) + 50)}</div>
            <CardDescription>
              Tracked across top 100 search results
            </CardDescription>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Top Competitors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{result.competitors.length}</div>
            <CardDescription>
              Direct competitors in search results
            </CardDescription>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Core Web Vitals</CardTitle>
          <CardDescription>Performance metrics that matter for rankings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Largest Contentful Paint</span>
                <Badge variant={Math.random() > 0.5 ? "outline" : "destructive"}>
                  {(Math.random() * 3 + 1.5).toFixed(1)}s
                </Badge>
              </div>
              <Progress value={Math.random() * 100} className="h-2" />
              {isMobile && <p className="text-xs text-muted-foreground">Time until largest content element loads</p>}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">First Input Delay</span>
                <Badge variant="outline">
                  {Math.floor(Math.random() * 100 + 10)}ms
                </Badge>
              </div>
              <Progress value={Math.random() * 100} className="h-2" />
              {isMobile && <p className="text-xs text-muted-foreground">Time until page becomes interactive</p>}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Cumulative Layout Shift</span>
                <Badge variant={Math.random() > 0.7 ? "outline" : "destructive"}>
                  {(Math.random() * 0.25).toFixed(2)}
                </Badge>
              </div>
              <Progress value={Math.random() * 100} className="h-2" />
              {isMobile && <p className="text-xs text-muted-foreground">Measures visual stability of page elements</p>}
            </div>
          </div>
          
          {!isMobile && (
            <div className="grid grid-cols-3 gap-6 mt-3">
              <p className="text-xs text-muted-foreground">Time until largest content element loads</p>
              <p className="text-xs text-muted-foreground">Time until page becomes interactive</p>
              <p className="text-xs text-muted-foreground">Measures visual stability of page elements</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded">
        <p><strong>Note:</strong> This is a demonstration using simulated data. For a comprehensive SEO audit with real data from professional tools, please contact Los Angeles SEO Group at 323-784-1577.</p>
      </div>
    </div>
  );
};

export default OverviewTab;
